// Libs
import classNames from 'classnames/bind';
import { TFunction } from 'i18next';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography } from '@mui/material';
import { ChangeEvent, useEffect, useState } from 'react';
// Components, Layouts, Pages
import { BaseTable, Loading } from '~/components/common';
// Others
import { StorageEnum } from '~/utils/enum/base';
import { useAppDispatch } from '~/utils/redux/hooks';
import { IAccountReceivable } from '~/utils/interface/statistic';
import { IPaginationResponse } from '~/utils/interface/base';
import { DateFormatEnum, RoleEnum } from '~/utils/enum/common';
import { getStatisticOrderDepositNotRequired, getStatisticOrderUnpaidDeposit } from '~/thunks/statistic/statisticThunk';
import { IHeaderCellType, IQueryParamBase } from '~/utils/interface/common';
import { formatCurrencyUSD, formatDate, formatPhoneNumber, getFullName } from '~/utils/helper/common';
import { DEFAULT_CURRENT_PAGE, DEFAULT_LIMIT_PAGE_DASHBOARD, EMPTY_VALUE } from '~/utils/constants/common';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
// Styles, images, icons
import styles from './AccountsReceivable.module.scss';

type Props = {
  description?: string;
  isNotRequired?: boolean;
};

const columnsAccountReceivable = (
  t: TFunction,
  onDetailOrder: (data: IAccountReceivable) => void
): IHeaderCellType<IAccountReceivable>[] => {
  return [
    {
      id: 'order',
      label: t('table_header_title_order'),
      render: (record: IAccountReceivable) => {
        return (
          <Typography className={cx('textOrder')} onClick={() => onDetailOrder(record)}>
            {record?.id ? `#${record?.id}` : EMPTY_VALUE}
          </Typography>
        );
      },
    },
    {
      id: 'client',
      label: t('account_receivable_title_customer'),
      render: (record: IAccountReceivable) => {
        return getFullName({
          firstName: record?.client?.firstName,
          lastName: record?.client?.lastName,
        });
      },
    },
    {
      id: 'startDate',
      label: t('account_receivable_title_event_date'),
      render: (record: IAccountReceivable) => {
        return formatDate(record?.startDate || record?.endDate, DateFormatEnum.M_D_YYYY) || EMPTY_VALUE;
      },
    },
    {
      id: 'amountPaid',
      label: t('account_receivable_title_amount'),
      render: (record: IAccountReceivable) => {
        return formatCurrencyUSD({ value: record?.amountPaid || record?.amountDue, showDecimal: true });
      },
    },
    {
      id: 'phoneNumber',
      label: t('account_receivable_title_contact'),
      render: (record: IAccountReceivable) => (
        <Stack direction={'column'}>
          {record?.client?.clientContacts?.[0]?.email && (
            <Typography className={cx('textEmail')}>{record?.client?.clientContacts?.[0]?.email}</Typography>
          )}

          {record?.client?.clientContacts?.[0]?.phoneNumber && (
            <Typography className={cx('textPhone')}>
              {formatPhoneNumber(record?.client?.clientContacts?.[0]?.phoneNumber)}
            </Typography>
          )}
        </Stack>
      ),
    },
  ];
};

const cx = classNames.bind(styles);

const AccountsReceivable = (props: Props) => {
  //#region Destructuring Props
  const { description, isNotRequired = false } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/dashboard']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [pagination, setPagination] = useState<IPaginationResponse>();
  const [paramObject, setParamObject] = useState<IQueryParamBase>({
    page: DEFAULT_CURRENT_PAGE,
    limit: DEFAULT_LIMIT_PAGE_DASHBOARD,
  });

  const [accountsReceivableList, setAccountsReceivableList] = useState<IAccountReceivable[]>();
  const currentRole = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0];
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListTaskToday(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListTaskToday = (params: IQueryParamBase) => {
    setIsLoading(true);

    dispatch(isNotRequired ? getStatisticOrderDepositNotRequired(params) : getStatisticOrderUnpaidDeposit(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination } = res.data;

        if (!responses) return;

        setAccountsReceivableList(responses);
        setPagination(pagination);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
  };

  const handleDetailOrder = (data?: IAccountReceivable) => {
    if (!data?.id) return;

    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(`${adminRouteAbsolute.detailOrder(data?.id)}?orderNumber=${data?.orderNumber}`);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(`${salesAdminRouteAbsolute.detailOrder(data?.id)}?orderNumber=${data?.orderNumber}`);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(`${salesPersonRouteAbsolute.detailOrder(data?.id)}?orderNumber=${data?.orderNumber}`);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(`${operationsManagerRouteAbsolute.detailOrder(data?.id)}?orderNumber=${data?.orderNumber}`);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(`${warehouseLeadRouteAbsolute.detailOrder(data?.id)}?orderNumber=${data?.orderNumber}`);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(`${warehouseStaffRouteAbsolute.detailOrder(data?.id)}?orderNumber=${data?.orderNumber}`);
        break;
      case RoleEnum.FOREMAN:
        navigate(`${foremanRouteAbsolute.detailOrder(data?.id)}?orderNumber=${data?.orderNumber}`);
        break;
      case RoleEnum.CREW:
        navigate(`${crewRouteAbsolute.detailOrder(data?.id)}?orderNumber=${data?.orderNumber}`);
        break;
      case RoleEnum.LOADER:
        navigate(`${loaderRouteAbsolute.detailOrder(data?.id)}?orderNumber=${data?.orderNumber}`);
        break;
    }
  };
  //#endregion Handle Function

  return (
    <Box id='accountsReceivableComponent' className={cx('container')}>
      <Stack direction={'column'} spacing={'13px'}>
        <Typography className={cx('title')}>{t('dashboard_title_accounts_receivable')}</Typography>

        <Typography className={cx('description')}>{description}</Typography>
      </Stack>

      <Box className={cx('body')}>
        <BaseTable
          dataSource={accountsReceivableList}
          currentPage={paramObject.page}
          totalPage={pagination?.totalPages}
          columns={columnsAccountReceivable(t, handleDetailOrder)}
          onPageChange={handlePageChange}
        />
      </Box>

      {isLoading && <Loading />}
    </Box>
  );
};

export default AccountsReceivable;
